export interface Industry {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
}

export interface BusinessCategory {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
}

/** How the merchant is billed. Mirrors Watchman's ContractType enum (PE-2079). */
export type ContractType = 'COMMISSION' | 'OUTRIGHT' | 'OUTRIGHT_INSTALMENT';

/**
 * `saleType` on `POST /merchants` (PE-2252) — a narrower creation-time enum than the full
 * `ContractType` above (no `OUTRIGHT_INSTALMENT`; that's a read-only legacy value on
 * pre-existing merchants). Mirrors watchman's `CreateMerchantDto.saleType`.
 */
export type SaleType = 'COMMISSION' | 'OUTRIGHT';

/** Invoicing frequency for the merchant. Mirrors Watchman's InvoicingCadence enum (PE-2079). */
export type InvoicingCadence = 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';

/** Cadence of an OUTRIGHT merchant's recurring platform fee (PE-2252). Mirrors watchman's
 *  `PlatformFeeFrequency` enum — deliberately narrower than `InvoicingCadence` (no WEEKLY). */
export type PlatformFeeFrequency = 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';

export const PLATFORM_FEE_FREQUENCY_LABELS: Record<PlatformFeeFrequency, string> = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ANNUALLY: 'Annually',
};

/**
 * How an OUTRIGHT merchant pays off its `customBuildPrice` (PE-2252). Mirrors watchman's
 * `InstallmentPlanDto` — `type` is the discriminator; exactly one of the two field groups
 * is populated depending on it.
 */
export type InstallmentPlan =
  | { type: 'MONTHLY'; installmentCount: number; startDate: string }
  | { type: 'CUSTOM'; installments: { dueDate: string; amount: number }[] };

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  COMMISSION: 'Commission',
  OUTRIGHT: 'Outright',
  OUTRIGHT_INSTALMENT: 'Outright (Instalment)',
};

export const INVOICING_CADENCE_LABELS: Record<InvoicingCadence, string> = {
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ANNUALLY: 'Annually',
};

/**
 * How a late payment penalty is calculated once an invoice passes its due date + grace
 * period. Mirrors Watchman's `LateFeeType` enum (confirmed against
 * watchman prisma/schema/merchant.model.prisma): FLAT = a minor-currency-unit amount;
 * PERCENTAGE = basis points of the invoice total (150 = 1.5%). null ⇒ no penalty is charged.
 */
export type LateFeeType = 'FLAT' | 'PERCENTAGE';

export const LATE_FEE_TYPE_LABELS: Record<LateFeeType, string> = {
  FLAT: 'Flat amount',
  PERCENTAGE: 'Percentage of total',
};

/**
 * The caller's own contract terms (PE-2098), returned by osprey-api
 * `GET /merchants/me/contract` (proxying watchman). Read-only — no merchant-scoped
 * write path exists for these fields. Amounts are in minor units (kobo/pence);
 * `commissionCharge` is a decimal string (e.g. "0.1500" = 15%).
 *
 * This is the LEGACY `invoicingSchedule` JSON column (pre-PE-2252). PE-2252 superseded it
 * for admin-created merchants — an OUTRIGHT sale's terms now live in the dedicated
 * `customBuildPrice` / `platformFee` / `platformFeeFrequency` / `installmentPlan` fields on
 * `Merchant` (see below) instead. This type is kept for merchants created before PE-2252.
 */
export interface MerchantInvoicingSchedule {
  /** One-time platform fee, minor units. */
  oneTimeFeeAmount?: number;
  /** ISO date the one-time fee is due. */
  oneTimeFeeDate?: string;
  /** Recurring platform fee, minor units. */
  recurringFeeAmount?: number;
  /** Interval between recurring charges, in days. */
  recurringIntervalDays?: number;
  /** Total number of instalments (OUTRIGHT_INSTALMENT only). */
  instalmentCount?: number;
}

export interface MerchantContractTerms {
  contractType: ContractType;
  /** Commission rate as a decimal string, e.g. "0.1500" = 15%. */
  commissionCharge: string;
  invoicingCadence: InvoicingCadence;
  /** Platform-fee schedule for non-commission merchants; null for COMMISSION. */
  invoicingSchedule: MerchantInvoicingSchedule | null;
  contractStartDate: string;
  contractEndDate: string | null;
  nextContractReviewDate: string | null;
  /** When these contract terms were last changed. */
  updatedAt: string;
}

export interface StaffUser {
  id: string;
  fullName: string;
  email: string;
  role: { name: string };
}

export interface Merchant {
  id: string;
  name: string;
  slug: string;
  companyAddress: string;
  commissionCharge: number;
  active: boolean;
  contractStartDate: string;
  contractEndDate: string | null;
  nextContractReviewDate: string | null;
  partnerSuccessBlueprintUrl: string | null;
  /** Microsoft Clarity project id for this merchant's storefront (session recordings). */
  clarityProjectId: string | null;
  bpmOfficerId: string | null;
  liaisonId: string | null;
  bpmOfficer: { id: string; fullName: string; email: string } | null;
  liaison: { id: string; fullName: string; email: string } | null;
  solutionId: string;
  contractType: ContractType;
  invoicingCadence: InvoicingCadence;
  /** Legacy platform-fee schedule (pre-PE-2252). null for merchants created after PE-2252 —
   *  see customBuildPrice/platformFee/platformFeeFrequency/installmentPlan instead. */
  invoicingSchedule: MerchantInvoicingSchedule | null;
  /** One-time custom-build price, minor currency units (PE-2252). null for COMMISSION merchants. */
  customBuildPrice: number | null;
  /** Recurring platform fee, minor currency units (PE-2252). null for COMMISSION merchants. */
  platformFee: number | null;
  /** Cadence of the recurring platformFee charge (PE-2252). null for COMMISSION merchants. */
  platformFeeFrequency: PlatformFeeFrequency | null;
  /** How customBuildPrice is paid off (PE-2252). null for COMMISSION merchants. */
  installmentPlan: InstallmentPlan | null;
  /** Late-payment penalty config (PE-2105). Null/undefined = platform default applies. */
  lateFeeType?: LateFeeType | null;
  /** Minor units (kobo) when lateFeeType is FLAT; basis points of the total (150 = 1.5%) when PERCENTAGE. */
  lateFeeAmount?: number | null;
  /** Days past dueDate before a late fee starts accruing. */
  lateFeeGracePeriodDays?: number | null;
  /** Days a pending invoice's quoted price stays valid before it's flagged Expired. */
  invoicePriceExpiryDays?: number | null;
  createdAt: string;
  updatedAt: string;
  industries: { industry: Industry }[];
  businessCategories: { businessCategory: BusinessCategory }[];
  invitePending: boolean;
  /** When the onboarding invite was last (re)sent — null if never invited. */
  lastInvitedAt: string | null;
}

export interface InviteMerchantPayload {
  merchantAdminName: string;
  email: string;
  storeName: string;
  commissionCharge: number;
  contractStartDate: string;
  contractEndDate?: string;
  industryIds: string[];
  businessCategoryIds: string[];
}

/**
 * Merchant creation (PE-2079, narrowed by PE-2250/PE-2252) — `POST /merchants`. Mirrors
 * watchman's `CreateMerchantDto` (src/domain/merchants/dto/merchant.dto.ts) field-for-field;
 * that DTO's cross-field validation (`MerchantsService.validateSaleTypeShape`) REJECTS the
 * wrong branch's fields with a 400 — e.g. sending `contractEnd` when `saleType` is OUTRIGHT,
 * or `customBuildPrice` when it's COMMISSION — so the two branches below are mutually
 * exclusive on the wire, not just in the UI.
 *
 * The collective admin captures only what the collective side must set — the merchant
 * administrator's own full name and phone number are NOT collected here; they're set by the
 * administrator themselves on their post-invite profile-completion screen (see
 * `ClaimInvitePayload` below).
 */
export interface CreateMerchantPayload {
  name: string;
  companyAddress: string;
  /** PE-2252 — how this merchant is sold. Replaces the old `contractType` field on this
   *  create payload (contractType is still the read-side field name on `Merchant`). */
  saleType: SaleType;
  /** Required when saleType is COMMISSION, decimal 0-1 (e.g. 0.15 = 15%). Must be omitted
   *  for OUTRIGHT. */
  commissionCharge?: number;
  /** COMMISSION only (optional, defaults to WEEKLY server-side). Omitted for OUTRIGHT — the
   *  recurring charge cadence there is `platformFeeFrequency` instead. */
  invoicingCadence?: InvoicingCadence;
  /** Required when saleType is COMMISSION. Must be omitted for OUTRIGHT. */
  contractEnd?: string;
  /** Required when saleType is COMMISSION. Must be omitted for OUTRIGHT. */
  nextReviewDate?: string;
  /** Required when saleType is OUTRIGHT, minor currency units. Must be omitted for COMMISSION. */
  customBuildPrice?: number;
  /** Required when saleType is OUTRIGHT, minor currency units. Must be omitted for COMMISSION. */
  platformFee?: number;
  /** Required when saleType is OUTRIGHT. Must be omitted for COMMISSION. */
  platformFeeFrequency?: PlatformFeeFrequency;
  /** How customBuildPrice is paid off. Required when saleType is OUTRIGHT. Must be omitted
   *  for COMMISSION. */
  installmentPlan?: InstallmentPlan;
  industryIds: string[];
  businessCategoryIds: string[];
  contractStartDate: string;
  partnerSuccessBlueprintUrl?: string;
  clarityProjectId?: string;
  bpmOfficerId?: string;
  liaisonId?: string;
  /** Required — the invite is sent here. Captured on the Basics step (moved from the old
   *  Administrator step, PE-2250). */
  adminEmail: string;
  solutionId?: string;
}

/**
 * Body for the invite-claim / profile-completion endpoint (`POST /merchants/onboard/claim`)
 * once a merchant sets their password (PE-2250). Mirrors watchman's
 * `ClaimMerchantInviteDto` exactly — note the field is `phoneNumber`, not `phone`.
 * `fullName`/`phoneNumber` used to be captured by the collective admin at creation time;
 * they're now supplied by the merchant administrator themselves, here.
 */
export interface ClaimInvitePayload {
  token: string;
  password: string;
  fullName: string;
  /** Optional — unlike fullName, this is not required. */
  phoneNumber?: string;
}

export interface UpdateMerchantPayload {
  name?: string;
  companyAddress?: string;
  commissionCharge?: number;
  industryIds?: string[];
  businessCategoryIds?: string[];
  contractStartDate?: string;
  contractEndDate?: string;
  nextContractReviewDate?: string;
  partnerSuccessBlueprintUrl?: string;
  bpmOfficerId?: string;
  liaisonId?: string;
  solutionId?: string;
  active?: boolean;
  invoicingCadence?: InvoicingCadence;
  lateFeeType?: LateFeeType | null;
  lateFeeAmount?: number | null;
  lateFeeGracePeriodDays?: number | null;
  invoicePriceExpiryDays?: number | null;
}

export interface MerchantApiKey {
  id: string;
  name: string;
  status: 'ACTIVE' | 'REVOKED' | 'EXPIRED';
  lastUsedAt: string | null;
  createdAt: string;
  expiresAt: string | null;
}

// Historical (incl. revoked) API keys carry the masked key value and revocation metadata.
export interface MerchantApiKeyHistory extends MerchantApiKey {
  key: string;
  revokedAt: string | null;
  revokedBy: { fullName: string; email: string } | null;
}

export interface MerchantUser {
  id: string;
  email: string;
  fullName: string;
  phoneNumber: string | null;
  isEmailVerified: boolean;
  isProfileCompleted: boolean;
  createdAt: string;
  role: { name: string; scope: string };
}

// Editable fields for a merchant user (watchman PATCH /users/:id). Only send what changed;
// the backend rejects an empty/no-op payload and a duplicate email (409).
export interface UpdateMerchantUserPayload {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
}
